import React from "react";
import { Users, Download, CheckCircle2, DollarSign, Clock, Heart, Ticket } from "lucide-react";
import { usd } from "../../lib/api";
import { Stat, PageTitle, sponsorBadge, sponsorDisplayName, sponsorState, sponsorStatusCls, SPONSOR_STATUS_OPTIONS, fmtTs } from "./adminUtils";
import { StatusChanger } from "./StatusChanger";

const PackageTotals = ({ packages }) => (
  <div data-testid="package-totals" className="mt-6 rounded-2xl bg-white border border-[#E2EBF0] p-5">
    <div className="font-display font-bold text-[#03281B]">Packages Sold</div>
    <div className="grid sm:grid-cols-3 gap-4 mt-3">
      {packages.map((p) => (
        <div key={p.key} data-testid={`package-total-${p.key}`} className="rounded-xl bg-[#F2F8F5] p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-[#008057]">{p.name} &middot; {usd(p.price)}</div>
          <div className="font-display text-3xl font-extrabold text-[#03281B] mt-1" data-testid={`package-sold-${p.key}`}>{p.sold}</div>
          <div className="text-xs text-slate-500">sold (paid) &middot; <span className="text-amber-700" data-testid={`package-pending-${p.key}`}>{p.pending} pending payment</span></div>
        </div>
      ))}
    </div>
  </div>
);

const SponsorStats = ({ stats, onExport }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
    <Stat icon={Heart} label="Total Sponsorships" value={stats.total_sponsorships} tone="gold" />
    <Stat icon={CheckCircle2} label="Confirmed" value={stats.confirmed} />
    <Stat icon={Users} label="Children Sponsored" value={stats.children_sponsored} />
    <Stat icon={Clock} label="Awaiting Assignment" value={stats.awaiting_assignment} tone="amber" />
    <Stat icon={DollarSign} label="Revenue" value={usd(stats.revenue)} />
    <Stat icon={DollarSign} label="Outstanding" value={usd(stats.outstanding)} tone="amber" />
    <Stat icon={Ticket} label="VIP Seats" value={stats.vip_seats} tone="gold" />
    <button data-testid="export-sponsors" onClick={() => onExport("sponsors")} className="rounded-2xl bg-white border border-[#E2EBF0] p-5 text-left hover:border-[#008057]"><Download className="text-[#008057]" /><div className="font-bold text-[#03281B] mt-2 text-sm">Export Sponsors CSV</div></button>
  </div>
);

const assignPrompt = (s, a, act) => {
  const em = window.prompt("Guardian email for the invitation:");
  const cn = window.prompt("Child's name (optional):");
  if (em) act(s.reference, `allocations/${a.alloc_id}/assign`, { guardian_email: em, child_name: cn });
};

const allocLabel = (a) => (a.mode === "nominate" ? `Nominated: ${a.nominee?.first_name || ""} ${a.nominee?.last_name || ""}` : "Organization selects");

const AllocationRow = ({ s, a, act }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
    <span className="text-slate-600">Alloc {a.alloc_id} &middot; {allocLabel(a)} &middot; <b>{a.status.replace(/_/g, " ")}</b>{a.child_name ? ` (${a.child_name})` : ""}</span>
    <span className="flex gap-2">
      {a.mode === "nominate" && a.status === "pending_review" && <button onClick={() => act(s.reference, `allocations/${a.alloc_id}/approve`)} className="text-[#008057] font-semibold" data-testid={`spons-approve-${a.alloc_id}`}>Approve</button>}
      {["awaiting_assignment", "approved", "pending_review"].includes(a.status) && <button onClick={() => assignPrompt(s, a, act)} className="text-[#008057] font-semibold" data-testid={`spons-assign-${a.alloc_id}`}>Assign & Invite</button>}
      {a.beneficiary_registration_id && <span className="text-emerald-600 text-xs">Registered ✓</span>}
    </span>
  </div>
);

const SponsorNotes = ({ s }) => (
  <div data-testid={`spons-${s.reference}-notes`} className="mt-3 text-xs text-slate-500 space-y-1">
    <div className="font-semibold uppercase tracking-widest text-[#008057]">Admin remarks</div>
    {s.notes.map((n) => <div key={`${n.ts}-${n.action}`}>{fmtTs(n.ts)} &middot; {n.actor} &middot; <b>{n.action}</b>{n.remark ? ` — ${n.remark}` : ""}</div>)}
  </div>
);

const SponsorActions = ({ s, act }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {s.status === "pending_payment" && <button onClick={() => act(s.reference, "confirm-payment")} className="rounded-full bg-[#008057] text-white px-4 py-1.5 text-sm font-bold" data-testid={`spons-confirm-${s.reference}`}>Confirm Payment</button>}
    {s.status !== "cancelled" && <button onClick={() => window.confirm(`Cancel sponsorship ${s.reference}?`) && act(s.reference, "cancel")} className="rounded-full border-2 border-red-300 text-red-600 px-4 py-1.5 text-sm font-bold" data-testid={`spons-cancel-${s.reference}`}>Cancel</button>}
    {s.scarf_status !== "fulfilled" && s.payment_status === "paid" && <button onClick={() => act(s.reference, "scarf-fulfilled")} className="rounded-full border-2 border-[#E2EBF0] px-4 py-1.5 text-sm" data-testid={`spons-scarf-${s.reference}`}>Mark Scarf Sent</button>}
    {s.vip_guests?.length > 0 && <span className="text-xs text-slate-500 self-center">VIP guests: {s.vip_guests.join(", ")}</span>}
  </div>
);

const SponsorshipCard = ({ s, act, reload }) => (
  <div className="rounded-2xl bg-white border border-[#E2EBF0] p-5" data-testid={`spons-card-${s.reference}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <span className="font-mono font-bold text-[#03281B]">{s.reference}</span>
        <span className="ml-2 text-sm text-slate-500">{sponsorDisplayName(s)} &middot; {s.package ? `${s.package.quantity} x ${s.package.name} · ` : ""}{s.quantity} child(ren) &middot; {usd(s.total)}</span>
      </div>
      <span className={`text-xs font-bold px-2 py-1 rounded-full ${sponsorStatusCls(s)}`}>{sponsorBadge(s)}</span>
    </div>
    <div className="text-xs text-slate-400 mt-1">Terms &amp; Privacy accepted: {s.terms_accepted_at ? `${fmtTs(s.terms_accepted_at, 19)} UTC` : "—"}</div>
    <SponsorActions s={s} act={act} />
    <StatusChanger kind="sponsorships" reference={s.reference} current={sponsorState(s)} statuses={SPONSOR_STATUS_OPTIONS} testPrefix={`spons-${s.reference}`} onDone={reload} />
    {s.notes?.length > 0 && <SponsorNotes s={s} />}
    {s.payment_status === "paid" && (
      <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
        {s.allocations.map((a) => <AllocationRow key={a.alloc_id} s={s} a={a} act={act} />)}
      </div>
    )}
  </div>
);

export const SponsorshipsTab = ({ spons, act, reload, onExport }) => (
  <div>
    <PageTitle>Sponsorship Program</PageTitle>
    <SponsorStats stats={spons.stats} onExport={onExport} />
    <PackageTotals packages={spons.stats.packages || []} />
    <div className="mt-6 space-y-4">
      {spons.sponsorships.map((s) => <SponsorshipCard key={s.reference} s={s} act={act} reload={reload} />)}
      {spons.sponsorships.length === 0 && <div className="text-center text-slate-400 py-10">No sponsorships yet.</div>}
    </div>
  </div>
);
